/**
 * Histórico do processo por ciclo da OS nativa no painel lateral do Detalhe OS 2.
 * Ciclo atual no topo (aberto); anteriores recolhidos e somente leitura.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Play } from 'lucide-react';
import { formatReincidenciaDataAbertura } from '../utils/orderDetailsReincidenciaUtils';
import '../../checkout-view/components/CheckoutViewModal.css';
import './OrderDetailsMockCiclos.css';

const fmt = (v) => (v ? formatReincidenciaDataAbertura(v, true) : '—');
const label = { fontSize: '10px', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--odm-muted)' };
const value = { fontSize: '12px', color: 'var(--odm-text)', lineHeight: 1.4, wordBreak: 'break-word' };
const thumb = {
  width: '56px', height: '56px', padding: 0, borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--odm-border)',
  background: 'var(--odm-surface-alt)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--odm-icon)', cursor: 'pointer',
};
const cycleChip = {
  display: 'inline-flex', alignItems: 'center', height: '22px', boxSizing: 'border-box', padding: '0 8px',
  borderRadius: '7px', border: '1px solid var(--odm-border)', background: 'var(--odm-return-bg)', color: 'var(--odm-return-text)',
  fontSize: '11px', fontWeight: 700,
};

function signatureSrc(svg) {
  const s = String(svg || '').trim();
  return s.startsWith('data:image/svg') ? s : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s)}`;
}

function galleryItems(cycle) {
  const items = (cycle.media || []).map((m, i) => ({
    url: m.url,
    isVideo: Boolean(m.isVideo),
    label: m.isVideo ? `Vídeo ${i + 1}` : `Foto ${i + 1}`,
  }));
  if (cycle.signature) {
    items.push({ url: signatureSrc(cycle.signature.svg), label: 'Assinatura', signature: true });
  }
  return items;
}

function Field({ title, children }) {
  if (!children) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={label}>{title}</span>
      <span style={value}>{children}</span>
    </div>
  );
}

function CicloBody({ cycle, steps, onOpen }) {
  const ordered = [...(steps || [])].reverse();
  const items = galleryItems(cycle);
  const openAt = (index) => onOpen?.(items, index);
  const times = [
    ['Início', cycle.startedAt],
    ['Fim', cycle.finishedAt],
    ['Efetivação', cycle.effectivatedAt],
  ].filter(([, at]) => at);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '4px 2px 6px' }}>
      <Field title="Técnico">{cycle.technician}</Field>
      <Field title="Condição">{cycle.condition}</Field>
      {times.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {times.map(([name, at]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ ...label, width: '84px', flexShrink: 0 }}>{name}</span>
              <span style={value}>{fmt(at)}</span>
            </div>
          ))}
        </div>
      ) : null}
      <Field title="Laudo técnico">
        {cycle.technicalReport ? <span style={{ whiteSpace: 'pre-wrap' }}>{cycle.technicalReport}</span> : null}
      </Field>
      <Field title="Peças">
        {cycle.parts?.length
          ? cycle.parts.map((p, i) => (
            <div key={`${p.code}-${i}`}>
              {p.code}{p.name ? ` - ${p.name}` : ''} · {p.quantity}{p.salesOrder ? ` · Pedido ${p.salesOrder}` : ''}
            </div>
          ))
          : null}
      </Field>
      <Field title="Leituras">
        {cycle.meters?.length ? cycle.meters.map((m) => <div key={m.name}>{m.name}: {m.value}</div>) : null}
      </Field>
      <Field title="Pedido vinculado">{cycle.salesOrders?.length ? cycle.salesOrders.join(', ') : null}</Field>
      {items.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {items.map((item, i) => (
            <button key={`${item.label}-${i}`} type="button" style={{ ...thumb, width: item.signature ? '110px' : '56px', background: item.signature ? '#ffffff' : thumb.background }} title={item.label} onClick={() => openAt(i)}>
              {item.isVideo ? (
                <Play size={16} strokeWidth={2} aria-label={item.label} />
              ) : (
                <img src={item.url} alt={item.label} loading="lazy" style={{ width: '100%', height: '100%', objectFit: item.signature ? 'contain' : 'cover' }} />
              )}
            </button>
          ))}
        </div>
      ) : null}
      {ordered.length ? (
        <div>
          <span style={label}>Processo</span>
          <div style={{ borderLeft: '2px solid var(--odm-border)', marginLeft: '4px', marginTop: '8px', paddingLeft: '14px' }}>
            {ordered.map((step, i) => (
              <div key={step.id} style={{ position: 'relative', marginBottom: '9px' }}>
                <span style={{ position: 'absolute', left: '-19px', top: '3px', width: '8px', height: '8px', borderRadius: '50%', background: i === 0 && cycle.isCurrent ? 'var(--odm-accent)' : 'var(--odm-dot)', border: '2px solid var(--odm-surface-soft)' }} />
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--odm-text)' }}>{step.label}</div>
                <div style={{ fontSize: '11px', color: 'var(--odm-muted)' }}>{step.date}</div>
                {step.observacoes ? <div style={{ fontSize: '11px', color: 'var(--odm-muted)', marginTop: '2px', lineHeight: 1.35 }}>{step.observacoes}</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FotoGallery({ osNumber, items, index, onIndex, onClose }) {
  const item = items[index];
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!item) return null;
  return createPortal(
    <div className="modal-overlay" style={{ zIndex: 100080 }} onClick={onClose}>
      <div className="checkout-view-fotos-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Fotos da OS ${osNumber || ''}`}>
        <div className="checkout-view-fotos-header">
          <div className="checkout-view-fotos-header-info">
            <h3 className="checkout-view-fotos-title">Fotos da OS {osNumber || '—'}</h3>
          </div>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="Fechar">✕</button>
        </div>
        <div className="checkout-view-fotos-body checkout-view-fotos-body--gallery">
          <div className="checkout-view-fotos-layout">
            <div className="checkout-view-fotos-thumbs">
              {items.map((thumbItem, i) => (
                <button key={`${thumbItem.label}-${i}`} type="button" className={`checkout-view-foto-thumb${i === index ? ' active' : ''}${thumbItem.isVideo ? ' checkout-view-foto-thumb--video' : ''}`} onClick={() => onIndex(i)} title={thumbItem.label}>
                  {thumbItem.isVideo ? (
                    <span className="checkout-view-foto-thumb-video" aria-hidden><Play size={18} strokeWidth={2.2} /></span>
                  ) : (
                    <img src={thumbItem.url} alt={thumbItem.label} style={thumbItem.signature ? { objectFit: 'contain', background: '#fff' } : undefined} />
                  )}
                </button>
              ))}
            </div>
            <div className="checkout-view-foto-main">
              <figure className="checkout-view-foto-item">
                <div className={item.signature ? 'checkout-view-assinatura-frame' : 'checkout-view-foto-wrapper'}>
                  {item.isVideo ? (
                    <video key={item.url} className="checkout-view-foto-img checkout-view-foto-video" src={item.url} controls playsInline preload="metadata" />
                  ) : (
                    <img src={item.url} alt={item.label} className={item.signature ? 'checkout-view-assinatura-img' : 'checkout-view-foto-img'} />
                  )}
                </div>
                <figcaption style={{ marginTop: '8px', fontSize: '12px', color: 'var(--odm-muted)' }}>{item.label}</figcaption>
              </figure>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CycleTitle({ cycle }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: 'var(--odm-title)' }}>
      <span style={cycleChip}>Ciclo {cycle.cycle}</span>
      {cycle.cycle === 1 ? 'Atendimento original' : null}
      {cycle.isCurrent ? 'Atual' : null}
    </span>
  );
}

/**
 * @param {{ cycles: array, steps: array, originalFocus?: number, osNumber?: string }} props
 */
export function OrderDetailsMockCiclos({ cycles, steps, originalFocus = 0, osNumber = '' }) {
  const originalRef = useRef(null);
  const originalCycle = Math.min(...cycles.map((c) => c.cycle));
  const [gallery, setGallery] = useState(null);

  useEffect(() => {
    if (originalFocus > 0) originalRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [originalFocus]);

  const openGallery = (items, index) => setGallery({ items, index });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {cycles.map((cycle) => {
        const title = <CycleTitle cycle={cycle} />;
        const body = <CicloBody cycle={cycle} steps={(steps || []).filter((s) => Number(s.cycle) === cycle.cycle)} onOpen={openGallery} />;
        const box = { border: '1px solid var(--odm-border)', borderRadius: '8px', padding: '10px 12px', background: 'var(--odm-chip-bg)' };
        return cycle.isCurrent ? (
          <section key={cycle.cycle} style={box}>
            <div style={{ marginBottom: '6px' }}>{title}</div>
            {body}
          </section>
        ) : cycle.cycle === originalCycle && originalFocus > 0 ? (
          <details key={`${cycle.cycle}-${originalFocus}`} ref={originalRef} open style={box}>
            <summary className="odm-ciclo-summary"><ChevronRight className="odm-ciclo-chevron" size={14} strokeWidth={2} aria-hidden />{title}</summary>
            {body}
          </details>
        ) : (
          <details key={cycle.cycle} style={box}>
            <summary className="odm-ciclo-summary"><ChevronRight className="odm-ciclo-chevron" size={14} strokeWidth={2} aria-hidden />{title}</summary>
            {body}
          </details>
        );
      })}
      {gallery ? (
        <FotoGallery
          osNumber={osNumber}
          items={gallery.items}
          index={gallery.index}
          onIndex={(index) => setGallery((prev) => (prev ? { ...prev, index } : prev))}
          onClose={() => setGallery(null)}
        />
      ) : null}
    </div>
  );
}
